import React, { useState } from 'react';
import { Shop, ReadingPeriod } from '../App';
import { ShopCard } from './ShopCard';
import { SaveIcon, CalendarIcon } from 'lucide-react';
interface MeterReadingFormProps {
  shops: Shop[];
  setShops: React.Dispatch<React.SetStateAction<Shop[]>>;
  onSaveReadings: () => void;
  selectedPeriod: ReadingPeriod;
  setSelectedPeriod: React.Dispatch<React.SetStateAction<ReadingPeriod>>;
  onPeriodChange: (period: ReadingPeriod) => void;
}
export const MeterReadingForm: React.FC<MeterReadingFormProps> = ({
  shops,
  setShops,
  onSaveReadings,
  selectedPeriod,
  setSelectedPeriod,
  onPeriodChange
}) => {
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [showPeriodSelector, setShowPeriodSelector] = useState(false);
  const handleReadingChange = (id: number, value: number) => {
    setShops(prevShops => prevShops.map(shop => shop.id === id ? {
      ...shop,
      currentReading: value
    } : shop));
  };
  const confirmSaveReadings = () => {
    setShowSaveConfirm(true);
  };
  const handleSaveReadings = () => {
    onSaveReadings();
    setShowSaveConfirm(false);
  };
  const handlePeriodChange = (newPeriod: ReadingPeriod) => {
    setSelectedPeriod(newPeriod);
    onPeriodChange(newPeriod);
    setShowPeriodSelector(false);
  };
  // Generate month options
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  // Generate year options (current year and 5 years before and after)
  const currentYear = new Date().getFullYear();
  const years = Array.from({
    length: 11
  }, (_, i) => currentYear - 5 + i);
  return <div className="space-y-4" data-id="element-118">
      <div className="flex justify-between items-center" data-id="element-119">
        <h2 className="text-xl font-semibold text-gray-800" data-id="element-120">Meter Readings</h2>
        <button onClick={() => setShowPeriodSelector(true)} className="flex items-center space-x-1 text-blue-600 text-sm border border-blue-300 rounded-lg px-3 py-1 hover:bg-blue-50" data-id="element-121">
          <CalendarIcon size={16} data-id="element-122" />
          <span data-id="element-123">
            {months[selectedPeriod.month]} {selectedPeriod.year}
          </span>
        </button>
      </div>
      <p className="text-sm text-gray-600" data-id="element-124">
        Enter the current meter readings for each shop
      </p>
      <div className="space-y-3" data-id="element-125">
        {shops.map(shop => <ShopCard key={shop.id} shop={shop} onReadingChange={value => handleReadingChange(shop.id, value)} data-id="element-126" />)}
      </div>
      <div className="pt-4" data-id="element-127">
        <button onClick={confirmSaveReadings} className="w-full py-3 bg-blue-600 text-white rounded-lg flex items-center justify-center space-x-2 shadow-md hover:bg-blue-700 transition-colors" data-id="element-128">
          <SaveIcon size={18} data-id="element-129" />
          <span data-id="element-130">Save Readings</span>
        </button>
      </div>
      {/* Period Selector Modal */}
      {showPeriodSelector && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-131">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm" data-id="element-132">
            <h3 className="text-lg font-semibold mb-4" data-id="element-133">
              Select Billing Period
            </h3>
            <div className="space-y-4" data-id="element-134">
              <div data-id="element-135">
                <label className="block text-sm text-gray-600 mb-1" data-id="element-136">
                  Month
                </label>
                <select value={selectedPeriod.month} onChange={e => setSelectedPeriod({
              ...selectedPeriod,
              month: parseInt(e.target.value)
            })} className="w-full px-3 py-2 border border-gray-300 rounded-md" data-id="element-137">
                  {months.map((month, index) => <option key={month} value={index} data-id="element-138">
                      {month}
                    </option>)}
                </select>
              </div>
              <div data-id="element-139">
                <label className="block text-sm text-gray-600 mb-1" data-id="element-140">Year</label>
                <select value={selectedPeriod.year} onChange={e => setSelectedPeriod({
              ...selectedPeriod,
              year: parseInt(e.target.value)
            })} className="w-full px-3 py-2 border border-gray-300 rounded-md" data-id="element-141">
                  {years.map(year => <option key={year} value={year} data-id="element-142">
                      {year}
                    </option>)}
                </select>
              </div>
            </div>
            <div className="flex space-x-3 mt-6" data-id="element-143">
              <button onClick={() => setShowPeriodSelector(false)} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-144">
                Cancel
              </button>
              <button onClick={() => handlePeriodChange(selectedPeriod)} className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-145">
                Apply
              </button>
            </div>
          </div>
        </div>}
      {/* Save Confirmation Modal */}
      {showSaveConfirm && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" data-id="element-146">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm" data-id="element-147">
            <h3 className="text-lg font-semibold mb-2" data-id="element-148">Confirm Save</h3>
            <p className="text-gray-600 mb-4" data-id="element-149">
              This will save readings for {months[selectedPeriod.month]}{' '}
              {selectedPeriod.year} and prepare for next month. Continue?
            </p>
            <div className="flex space-x-3" data-id="element-150">
              <button onClick={() => setShowSaveConfirm(false)} className="flex-1 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-151">
                Cancel
              </button>
              <button onClick={handleSaveReadings} className="flex-1 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-152">
                Confirm
              </button>
            </div>
          </div>
        </div>}
    </div>;
};